import { position, autoUpdate } from './floating.js';

/**
 * Keyboard and pointer behavior for [role="menu"] panels, following the WAI-ARIA menu pattern.
 * Shared by <mellow-dropdown> and <mellow-context>.
 */

const ITEM = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';

/** Items that belong to this menu (not to nested submenus). */
export function menuItems(menu) {
    return [...menu.querySelectorAll(ITEM)].filter((item) => item.closest('[role="menu"]') === menu && !item.closest('[hidden]'));
}

export function focusItem(menu, which = 'first') {
    const items = menuItems(menu);
    const item = which === 'last' ? items.at(-1) : items[0];
    (item ?? menu).focus();
}

function submenuOf(item) {
    return item?.getAttribute('aria-haspopup') === 'menu' ? item.parentElement.querySelector(':scope > [role="menu"]') : null;
}

export function openSubmenu(item, focus = true) {
    const submenu = submenuOf(item);
    if (!submenu) return;

    if (!submenu.matches(':popover-open')) {
        // Close sibling submenus first.
        item.closest('[role="menu"]')
            .querySelectorAll(':scope [role="menu"]:popover-open')
            .forEach((other) => other !== submenu && !other.contains(submenu) && other.hidePopover());
        submenu.showPopover();
    }
    if (focus) focusItem(submenu);
}

/**
 * Wire up a menu panel. `close` closes the whole menu (root popover), `restore` returns focus.
 */
export function setupMenu(root, { close }) {
    let typeahead = '';
    let typeaheadTimer;

    root.addEventListener('keydown', (event) => {
        const menu = event.target.closest('[role="menu"]');
        if (!menu || !root.contains(menu)) return;

        const items = menuItems(menu);
        const current = event.target.closest(ITEM);
        const index = items.indexOf(current);
        const rtl = getComputedStyle(menu).direction === 'rtl';
        const inward = rtl ? 'ArrowLeft' : 'ArrowRight';
        const outward = rtl ? 'ArrowRight' : 'ArrowLeft';

        const move = (to) => {
            event.preventDefault();
            items[(to + items.length) % items.length]?.focus();
        };

        switch (event.key) {
            case 'ArrowDown':
                return move(index + 1);
            case 'ArrowUp':
                return move(index === -1 ? items.length - 1 : index - 1);
            case 'Home':
                return move(0);
            case 'End':
                return move(items.length - 1);
            case inward:
                if (submenuOf(current)) {
                    event.preventDefault();
                    openSubmenu(current);
                }
                return;
            case outward:
                if (menu !== root) {
                    event.preventDefault();
                    const trigger = menu.parentElement.querySelector(':scope > [aria-haspopup="menu"]');
                    menu.hidePopover();
                    trigger?.focus();
                }
                return;
            case 'Escape':
                // Native popovers close themselves; we only restore focus for nested menus.
                if (menu !== root) {
                    event.preventDefault();
                    event.stopPropagation();
                    menu.hidePopover();
                    menu.parentElement.querySelector(':scope > [aria-haspopup="menu"]')?.focus();
                }
                return;
            case 'Tab':
                close(false);
                return;
            case ' ':
            case 'Enter':
                if (current && !['A', 'BUTTON'].includes(current.tagName)) {
                    // Checkbox / radio items are <div>s: activate them like buttons.
                    event.preventDefault();
                    current.click();
                } else if (current && current.tagName === 'A' && event.key === ' ') {
                    event.preventDefault();
                    current.click();
                } else if (submenuOf(current)) {
                    event.preventDefault();
                    openSubmenu(current);
                }
                return;
        }

        // Typeahead: jump to the next item starting with the typed characters.
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            clearTimeout(typeaheadTimer);
            typeahead += event.key.toLowerCase();
            typeaheadTimer = setTimeout(() => (typeahead = ''), 500);

            const ordered = [...items.slice(index + 1), ...items.slice(0, index + 1)];
            const match = ordered.find((item) => item.textContent.trim().toLowerCase().startsWith(typeahead));
            match?.focus();
        }
    });

    // Pointer: hovering focuses items (like native menus) and opens submenus.
    let hoverTimer;
    root.addEventListener('pointerover', (event) => {
        const item = event.target.closest(ITEM);
        if (!item || !root.contains(item) || event.pointerType === 'touch') return;

        if (item.getAttribute('aria-disabled') !== 'true') item.focus({ preventScroll: true });

        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
            // The item may have been replaced meanwhile (e.g. menus rebuilt by a Livewire update).
            const menu = item.isConnected ? item.closest('[role="menu"]') : null;
            if (!menu) return;
            if (submenuOf(item)) {
                openSubmenu(item, false);
            } else {
                menu.querySelectorAll(':scope [role="menu"]:popover-open').forEach((submenu) => submenu.hidePopover());
            }
        }, 120);
    });

    root.addEventListener('click', (event) => {
        const item = event.target.closest(ITEM);
        if (!item || !root.contains(item)) return;

        if (item.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            return;
        }

        if (submenuOf(item)) {
            openSubmenu(item);
            return;
        }

        // Checkbox / radio items toggle their (hidden) native input, so wire:model works.
        const input = item.querySelector(':scope > input');
        if (input && event.target !== input) {
            input.click();
            syncChoiceItems(root);
        }

        if (!item.hasAttribute('data-keep-open')) close(true);
    });

    // Submenus are positioned next to their trigger while open.
    root.addEventListener('toggle', (event) => {
        const submenu = event.target;
        if (submenu === root || !submenu.matches?.('[role="menu"]')) return;

        const trigger = submenu.parentElement.querySelector(':scope > [aria-haspopup="menu"]');
        if (event.newState === 'open') {
            trigger?.setAttribute('aria-expanded', 'true');
            submenu._cleanup = autoUpdate(trigger, submenu, () => position(trigger, submenu, { placement: 'right', align: 'start', offset: -4 }));
        } else {
            trigger?.setAttribute('aria-expanded', 'false');
            submenu._cleanup?.();
        }
    }, true);
}

/** Reflect hidden checkbox/radio inputs as aria-checked on menu items. */
export function syncChoiceItems(root = document) {
    root.querySelectorAll('[role="menuitemcheckbox"], [role="menuitemradio"]').forEach((item) => {
        const input = item.querySelector(':scope > input');
        if (input) item.setAttribute('aria-checked', String(input.checked));
    });
}
